import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check, drawnObjectCount, sharesADrawingSystem } from '../skillUsage';

export const iconSwapCycleSkill: CodeoSkill = {
    name: SKILL_NAMES.iconSwapCycle,
    kind: 'codeo',
    description:
        'Cycle several icons through one slot so a single position carries a whole category — for ' +
        '"any file type", "every channel", or a list of things that share a role.',
    documentUsesSkill: check(
        (script, markup) =>
            drawnObjectCount(markup) >= 2 &&
            sharesADrawingSystem(markup) &&
            /forEach|slice\(/.test(script),
    ),
    instructions: `# Icon Swap Cycle

One position in the frame holds a succession of icons, each replacing the last with a small vertical exchange. The slot never moves, so the viewer reads the *category* — these are all the same kind of thing — rather than four separate items.

It is the compact answer to "supports everything": far cleaner than a row of logos, and it keeps the frame's composition intact while carrying a list.

## Markup

Stack the icons in one fixed-size slot. Inline SVG so they inherit the palette colour.

\`\`\`html
<div id="icon-slot" style="position: absolute; left: 880px; top: 380px; width: 160px; height: 160px; overflow: hidden;">
  <svg class="cycle-icon" viewBox="0 0 48 48" style="position: absolute; inset: 0; width: 160px; height: 160px; fill: none; stroke: var(--l5-color-2, #38bdf8); stroke-width: 3; stroke-linecap: round; stroke-linejoin: round;">
    <path d="M14 4h14l8 8v32H14z" /><path d="M28 4v8h8" />
  </svg>
  <svg class="cycle-icon" viewBox="0 0 48 48" style="position: absolute; inset: 0; width: 160px; height: 160px; fill: none; stroke: var(--l5-color-2, #38bdf8); stroke-width: 3; stroke-linecap: round; stroke-linejoin: round;">
    <path d="M8 6v34h34" /><path d="M16 30l8-9 6 5 10-12" />
  </svg>
  <svg class="cycle-icon" viewBox="0 0 48 48" style="position: absolute; inset: 0; width: 160px; height: 160px; fill: none; stroke: var(--l5-color-2, #38bdf8); stroke-width: 3; stroke-linecap: round; stroke-linejoin: round;">
    <path d="M24 6L4 16l20 10 20-10L24 6z" /><path d="M4 24l20 10 20-10" />
  </svg>
</div>
<div id="icon-caption" style="position: absolute; left: 0; right: 0; top: 600px; text-align: center; font-family: var(--l5-font-0), sans-serif; font-size: 64px; font-weight: 700; color: var(--l5-color-0, #101820);">Whatever you already use</div>
\`\`\`

## Timeline code

Each icon leaves upward as the next rises in, so the slot reads as one rolling display.

\`\`\`js
const tl = gsap.timeline({ paused: true });
const icons = gsap.utils.toArray('.cycle-icon');
const FIRST = 0.8;
const DWELL = 0.9;

gsap.set(icons, { y: 120, opacity: 0 });
gsap.set(icons[0], { y: 0, opacity: 1 });

icons.slice(1).forEach((icon, index) => {
    const at = FIRST + index * DWELL;
    tl.to(icons[index], { y: -120, opacity: 0, duration: 0.34, ease: 'power3.in' }, at);
    tl.to(icon, { y: 0, opacity: 1, duration: 0.42, ease: 'power3.out' }, at + 0.08);
});

tl.from('#icon-caption', { y: 18, opacity: 0, duration: 0.5, ease: 'power3.out' }, 0.5);

window.__timelines = { main: tl };
\`\`\`

## Adapt

- **Dwell**: 0.8–1.0s per icon. Faster and the icons are not identified; slower and the cycle outlasts the point.
- **Land on one**: holding the final icon twice as long makes it the answer, with the earlier ones as context.
- **Scale exchange instead**: swapping by scaling out and in, rather than rolling, is quieter and works better when the icons differ a lot in silhouette.
- **Draw each on**: dash-drawing each icon as it arrives is richer but costs about 0.3s per icon — see \`${SKILL_NAMES.svgDrawOn}\`.
- **Caption swaps too**: pairing this with \`${SKILL_NAMES.wordSwapInPlace}\` on the caption names each icon, at the cost of a busier frame.
- **Icon set**: keep every icon at the same stroke weight, viewBox and optical size, or the slot appears to jump between them.

## Pitfalls

- Icons are \`y\`-tweened, so none may carry a CSS \`transform\`.
- The slot needs \`overflow: hidden\` so departing icons are clipped rather than colliding with the caption.
- Every icon must sit on the same optical centre inside its viewBox. A mark drawn slightly high will visibly bounce as the cycle runs.
- Three or four icons. More and the cycle takes longer than the scene can spare.
- Don't cycle icons that are not the same kind of thing. The slot asserts a category, and unrelated marks make it read as a slideshow in a box.
- Real brand logos in a cycle reproduce trademarks — use generic marks unless the video is entitled to those brands.
- The caption should be the constant. If both the icon and the caption change every beat, nothing anchors the frame.`,
};
